import { useEffect, useRef, useState } from 'react';
import dagre from 'dagre';

export default function GraphCanvas({ graph, onNodeSelect }) {
  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(0.8);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [layoutGraph, setLayoutGraph] = useState(null);
  const [showNames, setShowNames] = useState(false);
  const [graphBounds, setGraphBounds] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Create and layout graph using dagre
  useEffect(() => {
    if (!graph.nodes || graph.nodes.length === 0) return;

    try {
      // Create a new directed graph
      const g = new dagre.graphlib.Graph();

      // Set graph options
      g.setGraph({
        rankdir: 'TB', // Top to bottom
        nodesep: 50,   // Horizontal spacing
        ranksep: 80,   // Vertical spacing
        marginx: 20,
        marginy: 20
      });

      // Default node and edge labels
      g.setDefaultEdgeLabel(() => ({}));

      const nodeWidth = 200;
      const nodeHeight = 60;

      // Add nodes to graph
      graph.nodes.forEach(node => {
        g.setNode(node.name, {
          label: node.name,
          width: nodeWidth,
          height: nodeHeight,
          node: node
        });
      });

      // Add edges
      if (graph.edges && graph.edges.length > 0) {
        graph.edges.forEach(({ from, to }) => {
          if (g.node(from) && g.node(to)) {
            g.setEdge(from, to);
          }
        });
      } else {
        // Fallback: connect sequentially if no explicit edges provided
        for (let i = 0; i < graph.nodes.length - 1; i++) {
          const from = graph.nodes[i].name;
          const to = graph.nodes[i + 1].name;
          g.setEdge(from, to);
        }
      }

      // Run dagre layout algorithm
      dagre.layout(g);

      // Calculate graph bounds for centering and pan restrictions
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      g.nodes().forEach(v => {
        const node = g.node(v);
        minX = Math.min(minX, node.x - node.width / 2);
        maxX = Math.max(maxX, node.x + node.width / 2);
        minY = Math.min(minY, node.y - node.height / 2);
        maxY = Math.max(maxY, node.y + node.height / 2);
      });

      const bounds = {
        minX,
        maxX,
        minY,
        maxY,
        width: maxX - minX,
        height: maxY - minY,
        centerX: (minX + maxX) / 2,
        centerY: (minY + maxY) / 2
      };

      setGraphBounds(bounds);
      setLayoutGraph(g);

      console.log('[GraphCanvas] Laid out', g.nodeCount(), 'nodes and', g.edgeCount(), 'edges');
      console.log('[GraphCanvas] Graph bounds:', bounds);
    } catch (err) {
      console.error('[GraphCanvas] Dagre layout failed, falling back to grid layout:', err);

      // Fallback: simple grid layout so something renders even if dagre fails
      const fallback = new dagre.graphlib.Graph();
      fallback.setGraph({});
      const nodeWidth = 200;
      const nodeHeight = 60;
      const cols = 8;
      const hGap = 40;
      const vGap = 60;
      graph.nodes.forEach((node, idx) => {
        const col = idx % cols;
        const row = Math.floor(idx / cols);
        const x = col * (nodeWidth + hGap) + nodeWidth / 2;
        const y = row * (nodeHeight + vGap) + nodeHeight / 2;
        const label = {
          label: node.name,
          width: nodeWidth,
          height: nodeHeight,
          node: node,
          x,
          y
        };
        fallback.setNode(node.name, label);
      });
      if (graph.edges && graph.edges.length > 0) {
        graph.edges.forEach(({ from, to }) => {
          if (fallback.node(from) && fallback.node(to)) {
            fallback.setEdge(from, to);
          }
        });
      }
      setLayoutGraph(fallback);
    }
  }, [graph]);

  // Render the graph
  useEffect(() => {
    if (!canvasRef.current || !layoutGraph) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // Clear canvas with dark background
    ctx.fillStyle = '#404040';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate centered translation
    // Center the graph content in the canvas
    const centerX = graphBounds ? -graphBounds.centerX : 0;
    const centerY = graphBounds ? -graphBounds.centerY : 0;

    // Apply transformations
    ctx.save();
    ctx.translate(canvas.width / 2 + pan.x, canvas.height / 2 + pan.y);
    ctx.scale(zoom, zoom);
    ctx.translate(centerX, centerY);

    // Edge styling
    const edgeStroke = '#888';
    const nodeBorderStroke = '#1d1d1d';

    // Draw edges first
    layoutGraph.edges().forEach(e => {
      const edge = layoutGraph.edge(e);
      const sourceNode = layoutGraph.node(e.v);
      const targetNode = layoutGraph.node(e.w);

      ctx.strokeStyle = edgeStroke;
      ctx.lineWidth = 1;
      ctx.beginPath();

      // Use edge points if available, otherwise straight line
      if (edge.points && edge.points.length > 0) {
        ctx.moveTo(edge.points[0].x, edge.points[0].y);
        for (let i = 1; i < edge.points.length; i++) {
          ctx.lineTo(edge.points[i].x, edge.points[i].y);
        }
      } else {
        ctx.moveTo(sourceNode.x, sourceNode.y);
        ctx.lineTo(targetNode.x, targetNode.y);
      }

      ctx.stroke();

      // Draw arrow head
      const lastPoint = edge.points?.[edge.points.length - 1] || { x: targetNode.x, y: targetNode.y };
      const secondLast = edge.points?.[edge.points.length - 2] || { x: sourceNode.x, y: sourceNode.y };

      const angle = Math.atan2(lastPoint.y - secondLast.y, lastPoint.x - secondLast.x);
      const arrowSize = 8;

      ctx.fillStyle = edgeStroke;
      ctx.beginPath();
      ctx.moveTo(lastPoint.x, lastPoint.y);
      ctx.lineTo(
        lastPoint.x - arrowSize * Math.cos(angle - Math.PI / 6),
        lastPoint.y - arrowSize * Math.sin(angle - Math.PI / 6)
      );
      ctx.lineTo(
        lastPoint.x - arrowSize * Math.cos(angle + Math.PI / 6),
        lastPoint.y - arrowSize * Math.sin(angle + Math.PI / 6)
      );
      ctx.closePath();
      ctx.fill();
    });

    // Draw nodes
    layoutGraph.nodes().forEach(v => {
      const node = layoutGraph.node(v);
      const nodeData = node.node || { name: String(v), type: 'unknown' };

      // Check if this node is selected
      const isSelected = selectedNode && selectedNode.name === nodeData.name;

      const x = node.x - node.width / 2;
      const y = node.y - node.height / 2;

      // Determine colors for text
      const textColor = '#dfdfdf';

      // Prepare text content first
      const displayName = nodeData.name.length > 28 ? nodeData.name.substring(0, 25) + '...' : nodeData.name;
      const rawType = nodeData.type || nodeData.operator || null;

      // If type is null or 'unknown', fallback to using name as the main header
      let mainHeader, subHeader;
      if (!rawType || rawType === 'unknown' || rawType === 'null') {
        mainHeader = displayName;
        subHeader = null;
      } else {
        const truncatedType = rawType.length > 30 ? rawType.substring(0, 27) + '...' : rawType;
        const displayType = truncatedType.toLowerCase();
        mainHeader = displayType;
        subHeader = displayName;
      }

      // Adjust band height based on whether we're showing names
      const bandPaddingX = 12;
      const bandHeight = (showNames && subHeader) ? 40 : 22;
      const bandRadius = 6;
      const bandY = node.y - 8;
      const bandX = node.x - (node.width / 2) + bandPaddingX;
      const bandWidth = node.width - bandPaddingX * 2;

      // Draw type band behind header(s)
      ctx.beginPath();
      ctx.moveTo(bandX + bandRadius, bandY - bandHeight / 2);
      ctx.lineTo(bandX + bandWidth - bandRadius, bandY - bandHeight / 2);
      ctx.quadraticCurveTo(bandX + bandWidth, bandY - bandHeight / 2, bandX + bandWidth, bandY - bandHeight / 2 + bandRadius);
      ctx.lineTo(bandX + bandWidth, bandY + bandHeight / 2 - bandRadius);
      ctx.quadraticCurveTo(bandX + bandWidth, bandY + bandHeight / 2, bandX + bandWidth - bandRadius, bandY + bandHeight / 2);
      ctx.lineTo(bandX + bandRadius, bandY + bandHeight / 2);
      ctx.quadraticCurveTo(bandX, bandY + bandHeight / 2, bandX, bandY + bandHeight / 2 - bandRadius);
      ctx.lineTo(bandX, bandY - bandHeight / 2 + bandRadius);
      ctx.quadraticCurveTo(bandX, bandY - bandHeight / 2, bandX + bandRadius, bandY - bandHeight / 2);
      ctx.closePath();
      ctx.fillStyle = '#303030';
      ctx.fill();

      // Draw red outline if selected
      if (isSelected) {
        ctx.strokeStyle = '#ef4444'; // red-500
        ctx.lineWidth = 3;
        ctx.stroke();
      }

      // Draw text: type/operator as main header (lowercase), optional name as subtitle
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (showNames && subHeader) {
        // Show both main header and subtitle within the band
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        ctx.fillText(mainHeader, node.x, node.y - 16);

        // Subtitle in white, smaller font, within the same band
        ctx.font = '11px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        ctx.fillStyle = '#fff';
        ctx.fillText(subHeader, node.x, node.y + 2);
      } else {
        // Just show main header centered
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        ctx.fillText(mainHeader, node.x, node.y - 8);
      }
    });

    ctx.restore();

    // Draw zoom level
    ctx.fillStyle = '#6b7280';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(`Zoom: ${(zoom * 100).toFixed(0)}%`, 10, canvas.height - 10);

  }, [graph, layoutGraph, zoom, pan, showNames, graphBounds, selectedNode]);

  // Helper function to constrain pan within graph bounds
  const constrainPan = (newPan) => {
    if (!graphBounds || !canvasRef.current) return newPan;

    const canvas = canvasRef.current;
    const scaledWidth = graphBounds.width * zoom;
    const scaledHeight = graphBounds.height * zoom;

    // Add padding
    const padding = 100;

    // Calculate max pan limits to keep content visible
    const maxPanX = scaledWidth / 2 + padding;
    const maxPanY = scaledHeight / 2 + padding;
    const minPanX = -scaledWidth / 2 - padding;
    const minPanY = -scaledHeight / 2 - padding;

    return {
      x: Math.max(minPanX, Math.min(maxPanX, newPan.x)),
      y: Math.max(minPanY, Math.min(maxPanY, newPan.y))
    };
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.max(0.1, Math.min(3, prev * delta)));
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const newPan = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    };
    setPan(constrainPan(newPan));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Helper function to check if a point is inside a node
  const getNodeAtPosition = (canvasX, canvasY) => {
    if (!layoutGraph || !canvasRef.current || !graphBounds) return null;

    const canvas = canvasRef.current;

    // Convert canvas coordinates to graph coordinates
    const centerX = graphBounds ? -graphBounds.centerX : 0;
    const centerY = graphBounds ? -graphBounds.centerY : 0;

    // Reverse the transformation
    const graphX = (canvasX - canvas.width / 2 - pan.x) / zoom - centerX;
    const graphY = (canvasY - canvas.height / 2 - pan.y) / zoom - centerY;

    // Check each node
    for (const v of layoutGraph.nodes()) {
      const node = layoutGraph.node(v);
      const halfWidth = node.width / 2;
      const halfHeight = node.height / 2;

      if (
        graphX >= node.x - halfWidth &&
        graphX <= node.x + halfWidth &&
        graphY >= node.y - halfHeight &&
        graphY <= node.y + halfHeight
      ) {
        return node;
      }
    }

    return null;
  };

  const handleCanvasClick = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;

    const clickedNode = getNodeAtPosition(canvasX, canvasY);

    if (clickedNode) {
      // Find the original MXNet node data from rawGraph if available
      // This preserves the full node structure with op, attrs, inputs, etc.
      let originalNode = graph.nodes.find(n => n.name === clickedNode.node.name);

      // If we have access to the raw MXNet graph (for model graphs), use that instead
      if (graph.rawGraph && graph.rawGraph.nodes) {
        const rawNode = graph.rawGraph.nodes.find(n => n.name === clickedNode.node.name);
        if (rawNode) {
          originalNode = rawNode;
        }
      }

      setSelectedNode(originalNode);
      setSidebarOpen(true);
      
      // Notify parent component to navigate to the corresponding layer
      if (onNodeSelect) {
        onNodeSelect(originalNode);
      }
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(3, prev * 1.2));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(0.1, prev / 1.2));
  };

  const handleReset = () => {
    setZoom(0.8);
    setPan({ x: 0, y: 0 });
  };

  return (
    <div className="relative w-full h-full">
      {/* Controls */}
      <div className="absolute top-4 right-4 z-10 flex flex-col items-stretch gap-3">
        <label className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 text-sm shadow-lg cursor-pointer select-none">
          <input
            type="checkbox"
            className="accent-blue-600"
            checked={showNames}
            onChange={(e) => setShowNames(e.target.checked)}
          />
          <span className="text-gray-700">Show names</span>
        </label>
        <button
          onClick={handleZoomIn}
          className="px-3 py-2 bg-white rounded-md text-sm font-medium hover:bg-gray-50 shadow-lg border border-gray-200"
          title="Zoom In"
        >
          ➕
        </button>
        <button
          onClick={handleZoomOut}
          className="px-3 py-2 bg-white rounded-md text-sm font-medium hover:bg-gray-50 shadow-lg border border-gray-200"
          title="Zoom Out"
        >
          ➖
        </button>
        <button
          onClick={handleReset}
          className="px-3 py-2 bg-white rounded-md text-sm font-medium hover:bg-gray-50 shadow-lg border border-gray-200"
          title="Reset View"
        >
          ⟲
        </button>
      </div>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        className="w-full h-full cursor-move bg-gray-800"
        style={{ height: '100%' }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleCanvasClick}
      />


      {/* Stats */}
      <div className="absolute top-4 left-4 text-sm text-gray-300 bg-gray-800/95 px-4 py-3 rounded-lg shadow-lg border border-gray-700 backdrop-blur">
        <div className="font-bold mb-2 text-base">{graph.name || 'Model Graph'}</div>
        <div className="space-y-1.5 text-xs">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-green-200 border-2 border-green-500 rounded"></span>
            <span className="font-medium">Inputs:</span>
            <span className="text-gray-400">{graph.inputs?.length || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-blue-200 border-2 border-blue-500 rounded"></span>
            <span className="font-medium">Outputs:</span>
            <span className="text-gray-400">{graph.outputs?.length || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-gray-200 border-2 border-gray-500 rounded"></span>
            <span className="font-medium">Nodes:</span>
            <span className="text-gray-400">{graph.nodes?.length || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-gray-100 border-2 border-gray-400 rounded"></span>
            <span className="font-medium">Edges:</span>
            <span className="text-gray-400">{graph.edges?.length || 0}</span>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-gray-700 text-xs text-gray-500">
          Layout: Dagre
        </div>
      </div>

      {/* Node Properties Sidebar */}
      {sidebarOpen && selectedNode && (
        <div className="absolute top-0 right-0 h-full w-80 bg-[#2b2b2b] border-l border-gray-700 shadow-2xl z-20 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-[#1e1e1e]">
            <h3 className="text-white font-semibold text-sm uppercase tracking-wide">Node Properties</h3>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-white transition-colors"
              title="Close"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Type */}
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Type</div>
              <div className="bg-[#3a3a3a] px-3 py-2 rounded text-sm text-gray-200 font-mono">
                {selectedNode.type || selectedNode.operator || 'null'}
              </div>
            </div>

            {/* Name */}
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Name</div>
              <div className="bg-[#3a3a3a] px-3 py-2 rounded text-sm text-gray-200 font-mono">
                {selectedNode.name || 'unnamed'}
              </div>
            </div>

            {/* Operator (if different from type) */}
            {selectedNode.operator && selectedNode.operator !== selectedNode.type && (
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Operator</div>
                <div className="bg-[#3a3a3a] px-3 py-2 rounded text-sm text-gray-200 font-mono">
                  {selectedNode.operator}
                </div>
              </div>
            )}

            {/* Attributes */}
            {selectedNode.attrs && Object.keys(selectedNode.attrs).length > 0 && (
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Attributes</div>
                <div className="space-y-2">
                  {Object.entries(selectedNode.attrs).map(([key, value]) => (
                    <div key={key} className="bg-[#3a3a3a] px-3 py-2 rounded">
                      <div className="text-xs text-gray-400 mb-1 font-mono">{key}</div>
                      <div className="text-sm text-gray-200 font-mono break-all">
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </div>
                      <button
                        onClick={() => navigator.clipboard.writeText(typeof value === 'object' ? JSON.stringify(value) : String(value))}
                        className="text-xs text-blue-400 hover:text-blue-300 mt-1"
                        title="Copy to clipboard"
                      >
                        ➕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Inputs */}
            {selectedNode.inputs && selectedNode.inputs.length > 0 && (
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Inputs</div>
                <div className="space-y-2">
                  {selectedNode.inputs.map((input, idx) => (
                    <div key={idx} className="bg-[#3a3a3a] px-3 py-2 rounded">
                      <div className="text-xs text-gray-400 mb-1 font-mono">{idx}</div>
                      <div className="text-sm text-gray-200 font-mono">
                        name: [{Array.isArray(input) ? input.join(', ') : input}]
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Outputs */}
            {selectedNode.outputs && selectedNode.outputs.length > 0 && (
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Outputs</div>
                <div className="space-y-2">
                  {selectedNode.outputs.map((output, idx) => (
                    <div key={idx} className="bg-[#3a3a3a] px-3 py-2 rounded">
                      <div className="text-xs text-gray-400 mb-1 font-mono">{idx}</div>
                      <div className="text-sm text-gray-200 font-mono">
                        name: [{Array.isArray(output) ? output.join(', ') : output}]
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
